import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import core from "core";
import { ApplicationStatus, type PartnerApplicationApi, type PartnerFullApi } from "api/models/partner";
import api from "api";


export const useQueryPartners = (status?: ApplicationStatus[], page = 1) => {
	const take = 10;
	return useQuery({
		queryKey: ["partners", status, page],
		queryFn: () => api.admin.partner.search({ status, page, take }),
		placeholderData: keepPreviousData
	});
};

export const useQueryPartner = (id: string) => {
	return useQuery({
		queryKey: ["partner", id],
		queryFn: () => api.admin.partner.get(id)
	});
};

export const useQueryCms = () => {
	return useQuery({
		queryKey: ["partner-cms"],
		queryFn: () => api.admin.partner.getAllCms(),
		select: (data) => data.map(cms => ({ value: cms.id.toString(), label: cms.name }))
	});
};

export const useQueryPaywall = (cmsId?: string) => {
	return useQuery({
		queryKey: ["partner-paywall", cmsId],
		queryFn: () => api.admin.partner.getPaywalls(cmsId!),
		select: (data) => data.map(paywall => ({ value: paywall.id.toString(), label: paywall.name })),
		enabled: Boolean(cmsId)
	});
};

export const useQueryApplication = (id: string) => {
	return useQuery({
		queryKey: ["application", id],
		queryFn: () => api.admin.partner.getApplication(id)
	});
};

export const useMutateApplication = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, body, type }: { id: string, body: PartnerApplicationApi, type: "save" | "approve" | "reject" }) => {
			switch (type) {
				case "approve": return api.admin.partner.approve(id, body);
				case "reject": return api.admin.partner.reject(id, body);
				case "save": return api.admin.partner.save(id, body);
			}
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["partners"] })
	});
};

export const useMutatePartner = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, body}: { id: string, body: PartnerFullApi }) => api.admin.partner.update(id, body),
		onSuccess: (data, { id }) => {
			queryClient.setQueryData(["partner", id], data);
			setTimeout(() => core.scrollTo("#success-alert"), 200);
		},
		onError: () => setTimeout(() => core.scrollTo("#error-alert"), 200)
	});
};

export const useQueryCurrencies = () => {
	return useQuery({
		queryKey: ["currencies"],
		queryFn: () => api.admin.partner.getCurrencies(),
		select: (data) => data.map(currency => ({
			value: currency.id.toString(),
			label: currency.code + " - " + currency.name,
			symbol: currency.symbol
		}))
	});
};